import type { Chart as KChart, DeepPartial, KLineData } from "klinecharts";

import type { ChartAppearanceSettings } from "@/stores/settingsStore";
import type { ReplaySessionState } from "@/lib/lytick/replayStateMachine";

import {
  getChartSourceSnapshot,
  setChartSourceDepthCeiling,
  subscribeChartSource,
  type ChartSourceEvent,
} from "@/lib/lytick/chart-source-service";

import {
  getBaseColumns,
  getDisplayColumnsForTf,
  hasDataset,
} from "@/lib/lytick/chart-data-service";

import {
  sliceToCandles,
  searchTime,
  type CandleColumns,
} from "@/lib/lytick/columnar";

import {
  aggregatePartialBucket,
} from "@/lib/lytick/aggregate-columns";

import {
  getBucketStart,
  mergeTickIntoDisplaySeries,
  parseTimeframeToMinutes,
  resolveDisplayBucketGranularitySeconds,
  type Candle,
} from "@/lib/lytick/replay-utils";

// ─── Constants ────────────────────────────────────────────────────────────────

/** Initial / backward-pagination window size (bars). */
const WINDOW_SIZE = 5000;
/** When rawDisplaySeries exceeds this during replay, compact to WINDOW_SIZE. */
const MAX_ACCUMULATED = 10000;

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChartReplayState {
  isReplay: boolean;
  position: number;
  sessionState: ReplaySessionState;
  /** Version counter incremented each time new columns are loaded. */
  masterColumnsVersion: number;
  sourceGranularitySeconds: number;
  contextCandles: Candle[];
  contextGranularitySeconds: number;
}

interface ChartRuntimeView {
  symbol: string;
  timeframe: string;
  chartType: string;
}

function toKLine(candle: Candle): KLineData {
  return {
    timestamp: candle.timestamp,
    open: candle.open,
    high: candle.high,
    low: candle.low,
    close: candle.close,
    volume: candle.volume,
  };
}

function isHeikinAshi(chartType: string) {
  return chartType === "candle_heikin_ashi";
}

function candlesEqual(left: Candle, right: Candle) {
  return (
    left.timestamp === right.timestamp &&
    left.open === right.open &&
    left.high === right.high &&
    left.low === right.low &&
    left.close === right.close &&
    left.volume === right.volume
  );
}

function samePrefix(left: Candle[], right: Candle[], limit: number) {
  for (let index = 0; index < limit; index++) {
    const l = left[index];
    const r = right[index];
    if (!l || !r || !candlesEqual(l, r)) return false;
  }
  return true;
}

function buildHeikinAshiSeries(candles: Candle[]) {
  if (candles.length === 0) return [];
  const series: KLineData[] = [];
  let prevHAOpen = candles[0].open;
  let prevHAClose = candles[0].close;
  candles.forEach((candle) => {
    const haClose = (candle.open + candle.high + candle.low + candle.close) / 4;
    const haOpen = (prevHAOpen + prevHAClose) / 2;
    series.push({
      timestamp: candle.timestamp,
      open: haOpen,
      high: Math.max(candle.high, haOpen, haClose),
      low: Math.min(candle.low, haOpen, haClose),
      close: haClose,
      volume: candle.volume,
    });
    prevHAOpen = haOpen;
    prevHAClose = haClose;
  });
  return series;
}

function appendHeikinAshiCandle(previous: KLineData | null, candle: Candle): KLineData {
  const prevOpen = previous?.open ?? candle.open;
  const prevClose = previous?.close ?? candle.close;
  const close = (candle.open + candle.high + candle.low + candle.close) / 4;
  const open = (prevOpen + prevClose) / 2;
  return {
    timestamp: candle.timestamp,
    open,
    high: Math.max(candle.high, open, close),
    low: Math.min(candle.low, open, close),
    close,
    volume: candle.volume,
  } satisfies KLineData;
}

function getRenderSeries(candles: Candle[], chartType: string): KLineData[] {
  if (isHeikinAshi(chartType)) return buildHeikinAshiSeries(candles);
  return candles.map(toKLine);
}

function canApplyTailMutation(previous: Candle[], next: Candle[]) {
  if (previous.length === 0 || next.length === 0) return null;
  if (previous.length === next.length) {
    if (!samePrefix(previous, next, Math.max(0, previous.length - 1))) return null;
    return "replace_last" as const;
  }
  if (next.length === previous.length + 1) {
    if (!samePrefix(previous, next, previous.length)) return null;
    return "append" as const;
  }
  return null;
}

// ─── Windowed display helpers ─────────────────────────────────────────────────

/**
 * Build the display slice for replay mode at the given cursor position.
 * Uses the pre-aggregated tier for the display TF + a partial bucket for the
 * current in-progress bar. For the base TF, returns a simple window slice.
 */
function buildReplayDisplaySlice(
  base: CandleColumns,
  position: number,
  sourceGranularitySeconds: number,
  timeframe: string,
  windowStartHint: number,
): Candle[] {
  const clampedPos = Math.min(position, base.length - 1);
  if (clampedPos < 0) return [];

  const displayTfMinutes = parseTimeframeToMinutes(timeframe) ?? Math.max(1, sourceGranularitySeconds / 60);
  const baseTfMinutes = Math.max(1, sourceGranularitySeconds / 60);

  // For base TF: simple window slice of base columns
  if (displayTfMinutes <= baseTfMinutes) {
    const start = Math.max(0, windowStartHint);
    return sliceToCandles(base, start, clampedPos + 1);
  }

  // For coarser TF: use the pre-agg tier up to (but not including) current bucket
  const tierCols = getDisplayColumnsForTf(timeframe);
  if (!tierCols || tierCols === base) {
    // No tier found — fall back to base window
    return sliceToCandles(base, Math.max(0, windowStartHint), clampedPos + 1);
  }

  const cursorTs = base.time[clampedPos];
  const currentBucketStart = getBucketStart(cursorTs, displayTfMinutes);

  // All tier entries with timestamp < currentBucketStart are completed buckets
  const completedTierEnd = searchTime(tierCols, currentBucketStart);

  // Compute partial candle for the current in-progress bucket
  const partialStartBaseIdx = searchTime(base, currentBucketStart);
  const partial = aggregatePartialBucket(base, partialStartBaseIdx, clampedPos);

  const windowStart = Math.max(0, completedTierEnd - WINDOW_SIZE + (partial ? 1 : 0));
  const candles = sliceToCandles(tierCols, windowStart, completedTierEnd);

  if (partial) {
    candles.push({
      timestamp: currentBucketStart,
      open: partial.open,
      high: partial.high,
      low: partial.low,
      close: partial.close,
      volume: partial.volume,
    });
  }

  return candles;
}

// ─── ChartRuntime ─────────────────────────────────────────────────────────────

class ChartRuntime {
  readonly chartId: string;

  private chart: KChart | null = null;
  private subscribeBarCallback: ((data: KLineData) => void) | null = null;
  private view: ChartRuntimeView = {
    symbol: "R_100",
    timeframe: "1m",
    chartType: "candle_solid",
  };
  private replay: ChartReplayState = {
    isReplay: false,
    position: 0,
    sessionState: "live",
    masterColumnsVersion: 0,
    sourceGranularitySeconds: 60,
    contextCandles: [],
    contextGranularitySeconds: 86400,
  };
  private rawDisplaySeries: Candle[] = [];
  private renderSeries: KLineData[] = [];
  private sourceUnsubscribe: (() => void) | null = null;
  private disposed = false;
  private structuralResetCount = 0;
  /** Backward-pagination window start (index into active display tier). */
  private windowStartIndex = 0;

  constructor(chartId: string) {
    this.chartId = chartId;
    this.sourceUnsubscribe = subscribeChartSource((event) => {
      this.handleSourceEvent(event);
    });
  }

  attachChart(chart: KChart) {
    this.chart = chart;
  }

  applyAppearance(settings: ChartAppearanceSettings) {
    if (!this.chart) return;
    const styles: DeepPartial<import("klinecharts").Styles> = {
      grid: {
        show: settings.gridVisible,
        horizontal: { show: settings.gridVisible },
        vertical: { show: settings.gridVisible },
      },
      crosshair: { show: settings.crosshairStyle === "cross" },
    };
    this.chart.setStyles(styles);
  }

  getStructuralResetCount() {
    return this.structuralResetCount;
  }

  setSubscribeBarCallback(callback: ((data: KLineData) => void) | null) {
    this.subscribeBarCallback = callback;
  }

  setView(nextView: Partial<ChartRuntimeView>) {
    const previousView = this.view;
    this.view = { ...this.view, ...nextView };

    if (this.chart && previousView.symbol !== this.view.symbol) {
      this.chart.setSymbol({
        ticker: this.view.symbol,
        pricePrecision: 2,
        volumePrecision: 0,
      });
    }

    const requiresStructuralRefresh =
      previousView.symbol !== this.view.symbol ||
      previousView.timeframe !== this.view.timeframe ||
      isHeikinAshi(previousView.chartType) !== isHeikinAshi(this.view.chartType);

    if (requiresStructuralRefresh) {
      this.windowStartIndex = 0;
      this.rebuildAndReset({ allowEmptyReset: false });
    }
  }

  setReplayState(nextReplay: ChartReplayState) {
    const previousReplay = this.replay;
    this.replay = nextReplay;

    const modeChanged = previousReplay.isReplay !== nextReplay.isReplay;
    const sourceChanged =
      previousReplay.masterColumnsVersion !== nextReplay.masterColumnsVersion ||
      previousReplay.contextCandles !== nextReplay.contextCandles ||
      previousReplay.contextGranularitySeconds !== nextReplay.contextGranularitySeconds;

    if (modeChanged || sourceChanged) {
      this.windowStartIndex = 0;
      this.rebuildAndReset({ allowEmptyReset: modeChanged });
      return;
    }

    if (!nextReplay.isReplay) return;

    // Check if we need to compact the accumulated replay series
    if (this.rawDisplaySeries.length >= MAX_ACCUMULATED) {
      this.windowStartIndex = 0;
      this.rebuildAndReset({ allowEmptyReset: false });
      return;
    }

    const nextRawSeries = this.buildRawDisplaySeries();
    const mutationKind = canApplyTailMutation(this.rawDisplaySeries, nextRawSeries);

    if (mutationKind && nextReplay.position >= previousReplay.position && this.subscribeBarCallback) {
      this.rawDisplaySeries = nextRawSeries;
      if (mutationKind === "append") {
        this.applyAppendMutation(nextRawSeries[nextRawSeries.length - 1] ?? null);
        return;
      }
      this.applyReplaceLastMutation(nextRawSeries[nextRawSeries.length - 1] ?? null);
      return;
    }

    this.windowStartIndex = 0;
    this.rebuildAndReset({ allowEmptyReset: false });
  }

  getBars(type: "init" | "forward" | "backward" | "update") {
    if (type === "backward") {
      return this.handleBackwardPagination();
    }

    if (type === "forward" && !this.replay.isReplay) {
      return this.handleForwardPagination();
    }

    if (this.replay.isReplay) {
      return {
        data: type === "init" ? this.ensureRenderSeries() : [],
        more: { backward: false, forward: false },
      };
    }

    return {
      data: type === "init" ? this.ensureRenderSeries() : [],
      more: {
        backward: this.windowStartIndex > 0,
        forward: !getChartSourceSnapshot().hasReachedDepthCeiling,
      },
    };
  }

  dispose() {
    this.disposed = true;
    this.subscribeBarCallback = null;
    if (this.sourceUnsubscribe) {
      this.sourceUnsubscribe();
      this.sourceUnsubscribe = null;
    }
    this.chart = null;
  }

  // ─── Private ────────────────────────────────────────────────────────────────

  private buildRawDisplaySeries(): Candle[] {
    if (this.replay.isReplay) {
      const base = getBaseColumns();
      if (!base || base.length === 0) return [];

      return buildReplayDisplaySlice(
        base,
        this.replay.position,
        this.replay.sourceGranularitySeconds,
        this.view.timeframe,
        this.windowStartIndex,
      );
    }

    // Live (non-replay) display — windowed slice of the active display tier
    const tierCols = getDisplayColumnsForTf(this.view.timeframe);
    if (!tierCols || tierCols.length === 0) return [];

    const end = tierCols.length;
    const start = Math.max(this.windowStartIndex, end - WINDOW_SIZE);
    this.windowStartIndex = start;
    return sliceToCandles(tierCols, start, end);
  }

  private ensureRenderSeries() {
    const nextRawDisplaySeries = this.buildRawDisplaySeries();
    const renderSeries = getRenderSeries(nextRawDisplaySeries, this.view.chartType);
    this.rawDisplaySeries = nextRawDisplaySeries;
    this.renderSeries = renderSeries;
    return renderSeries;
  }

  private hasRenderableSource() {
    if (this.replay.isReplay) return hasDataset();
    return getChartSourceSnapshot().hasData;
  }

  private rebuildAndReset(options?: { allowEmptyReset?: boolean }) {
    if (this.disposed) return;
    this.ensureRenderSeries();
    if (!(options?.allowEmptyReset ?? true) && !this.hasRenderableSource()) return;
    if (this.chart) {
      this.structuralResetCount += 1;
      this.chart.resetData();
    }
  }

  private applyAppendMutation(lastRawCandle: Candle | null) {
    if (!lastRawCandle || !this.subscribeBarCallback) {
      this.rebuildAndReset({ allowEmptyReset: false });
      return;
    }
    if (isHeikinAshi(this.view.chartType)) {
      const prevRendered = this.renderSeries[this.renderSeries.length - 1] ?? null;
      const nextRendered = appendHeikinAshiCandle(prevRendered, lastRawCandle);
      this.renderSeries = [...this.renderSeries, nextRendered];
      this.subscribeBarCallback(nextRendered);
      return;
    }
    const nextRendered = toKLine(lastRawCandle);
    this.renderSeries = [...this.renderSeries, nextRendered];
    this.subscribeBarCallback(nextRendered);
  }

  private applyReplaceLastMutation(lastRawCandle: Candle | null) {
    if (!lastRawCandle || !this.subscribeBarCallback) {
      this.rebuildAndReset({ allowEmptyReset: false });
      return;
    }
    if (isHeikinAshi(this.view.chartType)) {
      const prevRendered = this.renderSeries.length >= 2 ? this.renderSeries[this.renderSeries.length - 2] : null;
      const nextRendered = appendHeikinAshiCandle(prevRendered, lastRawCandle);
      this.renderSeries = [...this.renderSeries.slice(0, -1), nextRendered];
      this.subscribeBarCallback(nextRendered);
      return;
    }
    const nextRendered = toKLine(lastRawCandle);
    this.renderSeries = [...this.renderSeries.slice(0, -1), nextRendered];
    this.subscribeBarCallback(nextRendered);
  }

  private handleSourceEvent(event: ChartSourceEvent) {
    if (this.disposed || this.replay.isReplay) return;
    if (event.snapshot.symbol !== null && event.snapshot.symbol !== this.view.symbol) return;

    switch (event.kind) {
      case "clear":
        this.windowStartIndex = 0;
        this.rebuildAndReset({ allowEmptyReset: true });
        break;
      case "replace":
        this.windowStartIndex = 0;
        this.rebuildAndReset({ allowEmptyReset: false });
        break;
      case "prepend":
        if (event.origin === this.chartId) return;
        break;
      case "tick":
        this.handleLiveTick(event.timestamp, event.price);
        break;
      case "loading":
        break;
    }
  }

  private handleLiveTick(timestamp: number, price: number) {
    if (!this.subscribeBarCallback) return;

    const mergeResult = mergeTickIntoDisplaySeries(
      this.rawDisplaySeries,
      timestamp,
      price,
      resolveDisplayBucketGranularitySeconds(this.view.timeframe),
      0,
    );

    if (!mergeResult.mutationKind) return;

    this.rawDisplaySeries = mergeResult.nextDisplayData;

    if (mergeResult.mutationKind === "append") {
      this.applyAppendMutation(mergeResult.nextDisplayData[mergeResult.nextDisplayData.length - 1] ?? null);
      return;
    }

    if (mergeResult.mutationKind === "replace_last") {
      this.applyReplaceLastMutation(mergeResult.nextDisplayData[mergeResult.nextDisplayData.length - 1] ?? null);
      return;
    }

    this.windowStartIndex = 0;
    this.rebuildAndReset({ allowEmptyReset: false });
  }

  private handleBackwardPagination(): { data: KLineData[]; more: { backward: boolean; forward: boolean } } {
    // Find the active display columns to page into
    const tierCols: CandleColumns | null = this.replay.isReplay
      ? (() => {
          const base = getBaseColumns();
          if (!base) return null;
          const tfMin = parseTimeframeToMinutes(this.view.timeframe) ?? 1;
          const baseTfMin = Math.max(1, this.replay.sourceGranularitySeconds / 60);
          return tfMin <= baseTfMin ? base : (getDisplayColumnsForTf(this.view.timeframe) ?? base);
        })()
      : getDisplayColumnsForTf(this.view.timeframe);

    if (!tierCols || this.windowStartIndex === 0) {
      return { data: [], more: { backward: false, forward: false } };
    }

    const prevStart = this.windowStartIndex;
    this.windowStartIndex = Math.max(0, prevStart - WINDOW_SIZE);
    const newBars = sliceToCandles(tierCols, this.windowStartIndex, prevStart);
    return {
      data: getRenderSeries(newBars, this.view.chartType),
      more: { backward: this.windowStartIndex > 0, forward: false },
    };
  }

  private async handleForwardPagination() {
    setChartSourceDepthCeiling(true);
    return { data: [], more: { backward: false, forward: false } };
  }
}

const runtimes = new Map<string, ChartRuntime>();

export function getOrCreateChartRuntime(chartId: string) {
  const existing = runtimes.get(chartId);
  if (existing) return existing;
  const runtime = new ChartRuntime(chartId);
  runtimes.set(chartId, runtime);
  return runtime;
}

export function getChartRuntime(chartId: string) {
  return runtimes.get(chartId) ?? null;
}

export function disposeChartRuntime(chartId: string) {
  const runtime = runtimes.get(chartId);
  if (!runtime) return;
  runtime.dispose();
  runtimes.delete(chartId);
}
